/**
 * Presentational verdict card for a publicly shared verification.
 *
 * Mirrors the verdict presentation in DashboardPage (seal + headline +
 * "{label} of the claim · {score}% confidence in our analysis" + lead +
 * sourcing chips + source pills) without any of its per-message state.
 * DashboardPage still renders its own inline copy — refactoring it onto
 * this component is a noted follow-up.
 */

import {
  VerdictSeal,
  toneForVerdict,
  toneForConfidence,
  confidenceBand,
  CONFIDENCE_TEXT_CLASS,
  CONFIDENCE_BAND_LABEL,
  VERDICT_LABEL,
  type BackendVerdict,
  type VerdictTone,
} from './VerdictSeal'
import SourcingChip from './SourcingChip'

const TONE_TEXT_CLASS: Record<VerdictTone, string> = {
  true: 'text-verdict-true',
  mixed: 'text-verdict-mixed',
  unverified: 'text-verdict-unverified',
  false: 'text-verdict-false',
}

export interface SharedVerdictSource {
  url: string
  title: string
  relevance: number
}

export interface VerdictCardProps {
  claim: string
  verdict?: BackendVerdict
  confidence: number
  headline?: string
  shortAnswer?: string
  confidenceBasis?: 'sources' | 'ai_knowledge' | 'web_search'
  knowledgeBasis?: string
  sources: SharedVerdictSource[]
  verifiedAt?: string
}

export function VerdictCard({
  claim,
  verdict,
  confidence,
  headline,
  shortAnswer,
  confidenceBasis,
  knowledgeBasis,
  sources,
  verifiedAt,
}: VerdictCardProps) {
  const hasVerdict = Boolean(verdict && verdict in VERDICT_LABEL)
  const tone = verdict && hasVerdict ? toneForVerdict(verdict) : toneForConfidence(confidence)
  const label = verdict && hasVerdict ? VERDICT_LABEL[verdict] : ''

  return (
    <div className="bg-white border border-paper-200 rounded-xl p-6 shadow-card">
      <div className="flex items-center gap-4 mb-4">
        <VerdictSeal confidence={confidence} tone={tone} size={56} />
        <div className="min-w-0">
          {headline && (
            <div className="font-display font-semibold text-ink-900 leading-snug">{headline}</div>
          )}
          <div className={`text-xs font-medium mt-1 ${TONE_TEXT_CLASS[tone]}`}>
            {hasVerdict && `${label} of the claim · `}
            <span className={CONFIDENCE_TEXT_CLASS[confidenceBand(confidence)]} title={CONFIDENCE_BAND_LABEL[confidenceBand(confidence)]}>
              {confidence}% confidence in our analysis
            </span>
          </div>
        </div>
      </div>

      <div className="text-sm text-ink-400 mb-3">
        Claim: <span className="text-ink-700">&ldquo;{claim}&rdquo;</span>
      </div>

      {shortAnswer && <div className="text-ink-700 mb-2">{shortAnswer}</div>}

      {confidenceBasis === 'web_search' && (
        <SourcingChip kind="web_search" knowledgeBasis={knowledgeBasis} />
      )}
      {confidenceBasis === 'ai_knowledge' && (
        <SourcingChip kind="ai_knowledge" knowledgeBasis={knowledgeBasis} />
      )}

      {sources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-paper-200">
          <div className="font-display text-[0.65rem] font-medium uppercase tracking-[0.18em] text-ink-400 mb-2">
            Sources ({sources.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {sources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                title={source.title}
                className="inline-flex items-center gap-1.5 max-w-full rounded-full bg-paper-50 border border-paper-200 px-3 py-1.5 text-xs text-ink-600 no-underline hover:border-accent-500 hover:text-accent-600 transition-colors duration-150"
              >
                <span className="truncate max-w-[220px]">{source.title}</span>
                <span className="font-medium text-ink-400 flex-shrink-0">· {source.relevance}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {verifiedAt && (
        <div className="text-xs text-ink-400 mt-4">
          Verified {new Date(verifiedAt).toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      )}
    </div>
  )
}
